/**
 * Financial Aid Value Objects: Circumstances
 *
 * Sections 9-11: Special Circumstances, Other Tuition, Major Expenditures
 *
 * @module financial-aid-domain/value-objects/financial-aid/circumstances
 */
import { z } from 'zod';
import { GradeLevel } from '../../types/enums';

// ============================================================================
// Section 9: Special Circumstances
// ============================================================================

export const SpecialCircumstancesSchema = z.object({
  hasUnusualExpenses: z.boolean().default(false),
  unusualExpensesDescription: z.string().nullable().optional(),
  hasRecentJobLoss: z.boolean().default(false),
  jobLossDetails: z.string().nullable().optional(),
  hasMedicalExpenses: z.boolean().default(false),
  medicalExpensesDetails: z.string().nullable().optional(),
  hasElderlyCare: z.boolean().default(false),
  elderlyCareDetails: z.string().nullable().optional(),
  hasDisabledDependent: z.boolean().default(false),
  disabledDependentDetails: z.string().nullable().optional(),
  hasDivorceAlimony: z.boolean().default(false),
  divorceAlimonyDetails: z.string().nullable().optional(),
  additionalCircumstances: z.string().nullable().optional(),
});
export type SpecialCircumstances = z.infer<typeof SpecialCircumstancesSchema>;

// ============================================================================
// Section 10: Other Tuition Obligations
// ============================================================================

export const OtherTuitionEntrySchema = z.object({
  schoolName: z.string().min(1),
  studentName: z.string().min(1),
  grade: GradeLevel.nullable().optional(),
  annualTuition: z.number().nonnegative(),
  scholarshipReceived: z.number().nonnegative().default(0),
  netTuition: z.number().nonnegative(), // Computed: annualTuition - scholarshipReceived
});
export type OtherTuitionEntry = z.infer<typeof OtherTuitionEntrySchema>;

export const OtherTuitionObligationsSchema = z.object({
  entries: z.array(OtherTuitionEntrySchema).default([]),
  totalOtherTuition: z.number().nonnegative().default(0), // Sum of netTuition
});
export type OtherTuitionObligations = z.infer<typeof OtherTuitionObligationsSchema>;

// ============================================================================
// Section 11: Major Expenditures (Planned)
// ============================================================================

export const MajorExpenditureSchema = z.object({
  description: z.string().min(1),
  expectedAmount: z.number().nonnegative(),
  expectedDate: z.number().int().nullable().optional(), // Unix timestamp
  reason: z.string().nullable().optional(),
});
export type MajorExpenditure = z.infer<typeof MajorExpenditureSchema>;

export const MajorExpendituresSchema = z.object({
  hasPlannedExpenditures: z.boolean().default(false),
  entries: z.array(MajorExpenditureSchema).default([]),
  totalPlannedExpenditures: z.number().nonnegative().default(0),
  hasSimchaPlanned: z.boolean().default(false), // Wedding, bar/bat mitzvah, etc.
  simchaDetails: z.string().nullable().optional(),
  servicesToYST: z.boolean().default(false),
  servicesDescription: z.string().nullable().optional(),
});
export type MajorExpenditures = z.infer<typeof MajorExpendituresSchema>;
